import {
  type FC,
  type ChangeEvent,
  type ReactNode,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState
} from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { cx } from "../../../utils/classnames";
import { has } from "../../../utils";
import { applyPhoneMask, demaskPhoneValue } from "./helpers/phone-mask";
import { isValidCharAtPosition } from "./helpers/zip-codes";

export enum InputSize {
  SM = "SM",
  BASE = "BASE"
}

const inputSize = {
  [InputSize.BASE]: "h-12",
  [InputSize.SM]: "h-10"
};

export enum ZipCountry {
  US = "US",
  CA = "CA"
}

const zipCodeMaxLength = {
  [ZipCountry.US]: 5,
  [ZipCountry.CA]: 7
};

interface DollarSignProps {
  isDisabledOrEmpty?: boolean;
  size?: InputSize;
}

const DollarSign = ({ isDisabledOrEmpty, size }: DollarSignProps) => (
  <span
    className={cx(
      "absolute left-[14px]",
      isDisabledOrEmpty ? "text-content-100" : "",
      size === InputSize.BASE ? "top-[12px]" : "top-[8px]"
    )}
  >
    $
  </span>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- we will change Icon props a lot
type UntitledIcon = FC<any>;

function trimMoneyString(money: string): string {
  if (money.includes(".")) {
    const [integerPart, decimalPart] = money.split(".");
    const trimmedIntegerPart =
      integerPart === "0" ? "0" : integerPart.replace(/^0+/, ""); // Remove leading zeros except if it's '0'
    return `${trimmedIntegerPart}.${decimalPart}`;
  }
  return money === "0" ? "0" : money.replace(/^0+/, ""); // Remove leading zeros except if it's '0'
}

const toMoney = (value: string | number) => {
  if (!value && value !== 0) return String(value);

  const moneyAmount = Number(value);
  const hasDecimal = moneyAmount % 1 !== 0;

  const numberFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    maximumFractionDigits: hasDecimal ? 2 : 0
  });

  return numberFormatter.format(moneyAmount);
};

const toIntlNumber = (value: string | number, fractionDigits = 2) => {
  if (!value && value !== 0) return String(value);

  const numberFormatter = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: fractionDigits
  });

  return numberFormatter.format(Number(value));
};

const countPossibleCommas = (str: string) => {
  const digitsOnly = str.replace(/\D/g, "");
  return Math.floor((digitsOnly.length - 1) / 3);
};

interface InputProps {
  label?: string | ReactNode;
  labelClassName?: string;
  size?: InputSize;
  id: string;
  placeholder?: string;
  type?: string;
  value?: string | number | null;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  className?: string;
  wrapperClassName?: string;
  error?: string | boolean;
  maxLength?: number;
  max?: number;
  checked?: boolean;
  defaultValue?: string;
  borderLess?: boolean;
  readOnly?: boolean;
  list?: string;
  name?: string;
  required?: boolean;
  autoComplete?: string;
  focusOnError?: boolean;
  hideOptional?: boolean;
  noIntlNormalization?: boolean;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  ErrorIcon?: UntitledIcon;
  optionalText?: string;
  zipCountry?: ZipCountry;
}

const IconBridge = ({
  Icon,
  HugeIcon,
  size,
  className
}: {
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  size?: InputSize;
  className?: string;
}) => {
  if (HugeIcon) {
    return (
      <HugeiconsIcon
        icon={HugeIcon}
        className={cx("shrink-0", className)}
        size={size === InputSize.BASE ? 20 : 16}
      />
    );
  }

  if (Icon) {
    return (
      <Icon
        className={cx("shrink-0", className)}
        size={size === InputSize.BASE ? "20" : "16"}
        stroke="inherit"
      />
    );
  }

  return null;
};

const WithIcon = ({
  Icon,
  HugeIcon,
  children,
  ErrorIcon,
  CurrencyIcon,
  wrapperClassName,
  isDisabledOrEmpty,
  size
}: {
  Icon?: UntitledIcon;
  HugeIcon?: IconSvgElement;
  children: ReactNode;
  ErrorIcon?: UntitledIcon;
  CurrencyIcon?: UntitledIcon;
  wrapperClassName?: string;
  size?: InputSize;
  isDisabledOrEmpty?: boolean;
}) => {
  return (
    <div className={cx("relative", wrapperClassName)}>
      {children}
      {CurrencyIcon ? (
        <CurrencyIcon isDisabledOrEmpty={isDisabledOrEmpty} size={size} />
      ) : null}
      {Icon || HugeIcon ? (
        <span className="absolute top-[14px] left-[14px] stroke-content-400">
          <IconBridge Icon={Icon} HugeIcon={HugeIcon} size={size} />
        </span>
      ) : null}
      {ErrorIcon ? (
        <ErrorIcon
          className="absolute top-4 right-4 stroke-danger-500"
          size={16}
        />
      ) : null}
    </div>
  );
};

const InputComponent = (
  props: InputProps,
  ref?: ForwardedRef<HTMLInputElement>
) => {
  const {
    label,
    labelClassName,
    id,
    placeholder,
    type,
    size = InputSize.BASE,
    onChange,
    value,
    className,
    wrapperClassName,
    error,
    borderLess,
    readOnly,
    required,
    hideOptional,
    focusOnError,
    noIntlNormalization,
    onFocus: _onFocus,
    onBlur: _onBlur,
    max,
    Icon,
    HugeIcon,
    optionalText,
    ErrorIcon,
    zipCountry,
    maxLength,
    ...rest
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const refFallback = useRef<HTMLInputElement>(null);
  const isCurrencyIcon = type === "money" && !Icon;

  useEffect(() => {
    if (error && focusOnError) {
      (
        (ref || refFallback) as unknown as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [error, focusOnError]);

  const onFocus = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsFocused(true);
      e.currentTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      if (_onFocus) {
        _onFocus(e);
      }
    },
    [_onFocus]
  );

  const onBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsFocused(false);
      if (
        (type === "number" || type === "money") &&
        (e.target.value === "." ||
          e.target.value.endsWith(".") ||
          e.target.value.startsWith("."))
      ) {
        if (e.target.value === ".") e.target.value = "";
        else if (e.target.value.endsWith("."))
          e.target.value = e.target.value.slice(0, -1);
        else if (e.target.value.startsWith("."))
          e.target.value = `0${e.target.value}`;

        onChange && onChange(e);
      }

      if (type === "percentage") {
        if (e.target.value.endsWith(".%")) {
          e.target.value = e.target.value.replace(".%", "%");
          onChange && onChange(e);
        }

        if (e.target.value === "%") {
          e.target.value = "";
          onChange && onChange(e);
        }
      }

      _onBlur && _onBlur(e);
    },
    [type, onChange, _onBlur]
  );

  const formattedValue = useMemo(() => {
    if (value === null || value === undefined) return undefined;
    if (isFocused && type === "money") return value;
    if (type === "tel") return applyPhoneMask(demaskPhoneValue(String(value)));
    if (type === "percentage") {
      if (String(value) === "%" || String(value) === "") return "";
      if (noIntlNormalization)
        return String(value).includes("%") ? String(value) : `${value}%`;

      return `${toIntlNumber(value, 0)}%`;
    }
    if ((type === "number" || type === "money") && !noIntlNormalization)
      return type === "number" ? toIntlNumber(value) : toMoney(value);

    return value;
  }, [type, value, noIntlNormalization, isFocused]);

  const formattedOnChange = !onChange
    ? undefined
    : (v: ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;

        if (type === "percentage") {
          const normalizedValue = v.target.value
            .replace(/[^0-9.]/g, "")
            .replace(/\.(?=.*\.)/g, ""); // Remove all dots except the first one;

          const cursorPosition = v.target.selectionStart;
          if (normalizedValue === "") {
            v.target.value = "";
          } else {
            v.target.value = `${normalizedValue}%`;
            setTimeout(() => {
              if (v.target.selectionStart) {
                v.target.setSelectionRange(cursorPosition, cursorPosition);
              }
            }, 0);
          }
        } else if (type === "tel") {
          const cursorPosition = v.target.selectionStart;
          if (v.target.value) {
            v.target.value = demaskPhoneValue(
              String(
                v.target.value.length > 14
                  ? v.target.value.substring(0, 14)
                  : v.target.value
              )
            );

            const diff =
              applyPhoneMask(v.target.value).length -
              applyPhoneMask(String(value)).length;

            onChange(v);

            if (cursorPosition) {
              setTimeout(() => {
                if (diff < 0) {
                  // PERFECT
                  v.target.setSelectionRange(cursorPosition, cursorPosition);
                } else if (cursorPosition === 1 || cursorPosition === 10) {
                  v.target.setSelectionRange(
                    cursorPosition + 1,
                    cursorPosition + 1
                  );
                } else if (cursorPosition === 5) {
                  v.target.setSelectionRange(
                    cursorPosition + 2,
                    cursorPosition + 2
                  );
                } else {
                  v.target.setSelectionRange(cursorPosition, cursorPosition);
                }
              }, 0);
            }

            return;
          }
        } else if (type === "zip") {
          if (zipCountry === ZipCountry.CA) {
            // Canadian zip code format: A1B 2C3
            const normalizedZip = v.target.value
              .toUpperCase()
              .replace(/[^A-Z0-9- ]/g, "");

            const result = Array.from(normalizedZip)
              .filter((char, idx) => isValidCharAtPosition(char, idx))
              .join("");

            v.target.value = result;
          } else if (zipCountry === ZipCountry.US) {
            // US zip code format: 12345
            const normalizedZip = v.target.value.replace(/[^0-9]/g, "");

            v.target.value = normalizedZip;
          }

          onChange(v);
        } else if (type === "number") {
          const cursorPosition = v.target.selectionStart;
          // Filter all non-numeric characters and leave . and ,
          let normalizedValue = v.target.value.replace(/[^0-9.,]/g, "");

          if (max && Number(normalizedValue) > max)
            normalizedValue = max.toString();

          v.target.value = normalizedValue
            .replace(/[^\d.]/g, "")
            .replace(/\.(?=.*\.)/g, "");

          onChange(v);

          if (!noIntlNormalization && cursorPosition && value) {
            setTimeout(() => {
              const diff =
                v.target.value.length - String(toMoney(value || 0)).length;
              const isPositionOnComma =
                v.target.value.replace(/[^0-9]/g, "") === String(value);

              if (isPositionOnComma && diff === 0) {
                v.target.setSelectionRange(cursorPosition, cursorPosition);
              } else if (diff < 0) {
                const diffComma =
                  countPossibleCommas(v.target.value) -
                  countPossibleCommas(String(value));
                if (diff < -1 && isPositionOnComma) {
                  v.target.setSelectionRange(
                    cursorPosition + diff + 1,
                    cursorPosition + diff + 1
                  );
                } else {
                  v.target.setSelectionRange(
                    cursorPosition + diffComma,
                    cursorPosition + diffComma
                  );
                }
              } else {
                v.target.setSelectionRange(
                  cursorPosition + diff,
                  cursorPosition + diff
                );
              }
            }, 0);
          }

          return;
        } else if (type === "money") {
          let normalizedValue = trimMoneyString(v.target.value)
            .replace(/[^\d.]/g, "") // Filter all non-numeric characters and leave .
            .replace(/(?:\.\d{0,2})\d*$/, (match) => match.slice(0, 3)); // Leave up to 2 numbers after .

          if (max && Number(normalizedValue) > max)
            normalizedValue = max.toString();

          v.target.value = normalizedValue;

          onChange(v);
          return;
        }

        onChange(v);
      };

  return (
    <>
      {!borderLess && label ? (
        <label
          className={cx(
            "text-content-700 mb-1 block text-sm font-[500]",
            labelClassName
          )}
          htmlFor={id}
        >
          {label}
          {!required && !readOnly && !hideOptional ? (
            <span className="text-content-100">
              {" "}
              {optionalText || "(Optional)"}
            </span>
          ) : null}
        </label>
      ) : null}

      {type === "tel" ? (
        <input
          id={id}
          name={id}
          ref={ref || refFallback}
          type="hidden"
          value={demaskPhoneValue(String(value))}
        />
      ) : null}

      <WithIcon
        Icon={Icon}
        CurrencyIcon={isCurrencyIcon ? DollarSign : undefined}
        HugeIcon={HugeIcon}
        ErrorIcon={error && ErrorIcon ? ErrorIcon : undefined}
        wrapperClassName={wrapperClassName}
        isDisabledOrEmpty={readOnly || !has(value) || value === ""}
        size={size}
      >
        <input
          className={cx(
            "placeholder:text-content-100 border-alpha-100 shadow-xs focus:border-primary-100 focus:shadow-focus-ring placeholder:text-content-100 border-alpha-100 mb-2 w-full rounded-md border border text-base font-medium placeholder:text-base placeholder:font-medium placeholder:font-normal focus:outline-0 focus:ring-0",
            error ? "text-danger-700 border-danger-300" : "",
            readOnly
              ? "bg-alpha-40 focus:!border-alpha-100 text-content-100 focus:shadow-none"
              : "",
            inputSize[size],
            borderLess
              ? "placeholder:text-content-100 border-0 border-transparent p-0 text-2xl text-gray-600 shadow-none placeholder:text-2xl placeholder:font-semibold placeholder:italic"
              : "p-2 px-3",
            isCurrencyIcon ? "pl-6" : "",
            Icon || HugeIcon ? "pl-10" : "",
            ErrorIcon ? "pr-10" : "",
            className
          )}
          id={id}
          inputMode={
            ["tel", "number", "money"].includes(type || "")
              ? "numeric"
              : undefined
          }
          max={max}
          name={id}
          onBlur={onBlur}
          onChange={formattedOnChange}
          onFocus={onFocus}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={ref || refFallback}
          type={["number", "money", "tel"].includes(type || "") ? "text" : type}
          value={formattedValue}
          maxLength={
            maxLength ??
            (type === "zip"
              ? zipCodeMaxLength[zipCountry || ZipCountry.US]
              : undefined)
          }
          {...rest}
        />
      </WithIcon>

      {error ? <p className="text-danger-500 text-xs">{error}</p> : null}
    </>
  );
};

const Input = forwardRef(InputComponent);

export { Input };
